import { Calendar, CheckCircle, XCircle, Hourglass, CheckSquare, Square } from "@phosphor-icons/react";
import { timeAgo } from "@/lib/format";

/**
 * Read-only widgets shared between the two requester-facing portal views
 * (magic-link `PortalTicketDetail` in Portal.jsx and the token-gated
 * `PublicTicket.jsx`). Renders the Key Date callout and the approval
 * status banner including the approver's comment when present.
 *
 * Consistent with how the checklist is rendered in the portal — visible,
 * informative, but not editable.
 */

export function PortalKeyDate({ ticket }) {
  if (!ticket?.key_date) return null;
  const nice = (() => {
    try {
      return new Date(ticket.key_date).toLocaleDateString("en-GB", {
        weekday: "long", day: "numeric", month: "long", year: "numeric",
      });
    } catch { return ticket.key_date; }
  })();
  const daysAway = Math.round((new Date(ticket.key_date) - new Date()) / 86400000);
  const relative =
    daysAway === 0 ? "today"
    : daysAway > 0 ? `in ${daysAway} day${daysAway === 1 ? "" : "s"}`
    : `${-daysAway} day${daysAway === -1 ? "" : "s"} ago`;

  return (
    <div className="hairline rounded-lg bg-[#4f4c90]/5 border-[#4f4c90]/20 p-4 flex items-center gap-4" data-testid="portal-key-date">
      <div className="w-14 h-14 rounded-md bg-white border border-[#4f4c90]/30 flex flex-col items-center justify-center shrink-0">
        <Calendar size={16} weight="duotone" className="text-[#4f4c90]" />
        <div className="text-[10px] font-mono text-[#4f4c90] mt-0.5 leading-none">
          {new Date(ticket.key_date).toLocaleDateString("en-GB", { month: "short", day: "numeric" })}
        </div>
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[11px] uppercase tracking-wider text-[#4f4c90] font-medium">
          {ticket.key_date_label || "Key date"}
        </div>
        <div className="text-sm text-slate-800 font-medium">{nice}</div>
        <div className="text-[11px] text-slate-500 font-mono">{relative}</div>
      </div>
    </div>
  );
}

export function PortalApprovalStatus({ ticket }) {
  const s = ticket?.approval_status;
  if (!s) return null;
  const pending = s === "pending";
  const approved = s === "approved";
  const rejected = s === "rejected";
  return (
    <div
      className={`hairline rounded-lg p-3 flex items-start gap-3 ${
        pending ? "bg-amber-50 border-amber-200"
        : approved ? "bg-emerald-50 border-emerald-200"
        : "bg-red-50 border-red-200"
      }`}
      data-testid="portal-approval-banner"
    >
      <div className="mt-0.5">
        {pending && <Hourglass size={18} weight="duotone" className="text-amber-500" />}
        {approved && <CheckCircle size={18} weight="fill" className="text-emerald-500" />}
        {rejected && <XCircle size={18} weight="fill" className="text-red-500" />}
      </div>
      <div className="flex-1 min-w-0">
        <div className={`text-[11px] uppercase font-medium tracking-wider ${
          pending ? "text-amber-800"
          : approved ? "text-emerald-800"
          : "text-red-800"
        }`}>
          {pending && "Awaiting manager approval"}
          {approved && "Approved by manager"}
          {rejected && "Rejected by manager"}
        </div>
        <div className="text-xs text-slate-700 mt-1">
          {pending && (
            <>Your request is with <b>{ticket.approval_decided_by_email || "your manager"}</b>. We&apos;ll start work as soon as they approve.</>
          )}
          {approved && (
            <>Approved by <b>{ticket.approval_decided_by_email}</b> {ticket.approval_decided_at ? timeAgo(ticket.approval_decided_at) : ""}.</>
          )}
          {rejected && (
            <>Rejected by <b>{ticket.approval_decided_by_email}</b> {ticket.approval_decided_at ? timeAgo(ticket.approval_decided_at) : ""}.</>
          )}
        </div>
        {ticket.approval_manager_note && (
          <div className="text-xs text-slate-700 mt-2 bg-white/60 border border-slate-200 rounded-md p-2 whitespace-pre-wrap" data-testid="portal-approval-comment">
            <span className="text-[10px] uppercase tracking-wider text-slate-500 font-medium">Comment</span>
            <div className="mt-0.5 italic">{ticket.approval_manager_note}</div>
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Read-only checklist for the portal ticket detail. Shows every
 * checklist item on the ticket with tick state — visible so requesters
 * (especially HR submitting onboarding forms) can see progress on the
 * ticket they raised, but non-interactive (agents drive the ticks).
 */
export function PortalChecklist({ items }) {
  if (!items || items.length === 0) return null;
  const done = items.filter((i) => i?.is_checked).length;
  const total = items.length;
  const pct = total > 0 ? Math.round((done / total) * 100) : 0;
  return (
    <div className="hairline rounded-lg bg-white p-4" data-testid="portal-checklist">
      <div className="flex items-baseline justify-between gap-2 mb-2">
        <div className="text-[11px] uppercase tracking-wider text-slate-500 font-medium">Checklist</div>
        <div className="text-[11px] text-slate-500 font-mono" data-testid="portal-checklist-count">{done} of {total} done</div>
      </div>
      <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden mb-3">
        <div
          className={`h-full ${pct >= 100 ? "bg-emerald-500" : pct > 0 ? "bg-amber-500" : "bg-red-400"}`}
          style={{ width: `${pct}%` }}
        />
      </div>
      <ul className="space-y-1">
        {items.map((it, idx) => {
          const checked = !!it?.is_checked;
          return (
            <li
              key={it?.id || idx}
              data-testid="portal-checklist-item"
              className={`flex items-start gap-2 text-sm py-1 ${checked ? "text-slate-500 line-through" : "text-slate-800"}`}
            >
              {checked
                ? <CheckSquare size={16} weight="fill" className="text-emerald-500 mt-0.5 shrink-0" />
                : <Square size={16} weight="regular" className="text-slate-400 mt-0.5 shrink-0" />}
              <span className="flex-1 min-w-0">{it?.label || it?.text || ""}</span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
